import { CostCenter, CostCenterTypeEnum, RightEnum } from "@finway-group/shared/lib/models"
import { useSelector } from "react-redux"
import { AuthzService, UserService } from "~Shared/services"
import { store } from "~Shared/store"
import { type RootState } from "~Shared/store/rootState.interface"
import { findSearchResultParentCostCenters } from "~Shared/utils/costCenter.utils"
import { useEmployees } from "./employee.hooks"

export const useCostCenters = (excludeDeleted: boolean = false, filtered: boolean = false, searchValue?: string) => {
    const costCenters = useSelector(({ costCenters }: RootState) => costCenters.items)
    const employees = useEmployees()
    return filterCostCenters(costCenters, employees, excludeDeleted, filtered, searchValue)
}

export const getCostCentersFromStore = (excludeDeleted: boolean = false, filtered: boolean = false, searchValue?: string) => {
    const costCenters = store.getState().costCenters.items
    const employees = store.getState().employees.items
    return filterCostCenters(costCenters, employees, excludeDeleted, filtered, searchValue)
}

const filterCostCenters = (costCenters: Array<any>, employees: Array<any>, excludeDeleted: boolean = false, filtered: boolean = false, searchValue?: string) => {
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const allCostCenters = [...costCenters]

    if (excludeDeleted) {
        costCenters = costCenters?.filter((costCenter: CostCenter) => !costCenter.deleted)
    }

    if (filtered) {
        const ccIdMap = new Map<string, CostCenter>(costCenters.map((cc) => [cc._id, cc]))
        costCenters = costCenters.filter(
            (cc: CostCenter) =>
                // admin
                AuthzService.isRightGrantedForLoggedInUser(RightEnum.CC1__ALL__READ) ||
                // responsible user
                cc.responsibleUser === loggedInUser.id ||
                (cc.parentCostCenter && ccIdMap.get(cc.parentCostCenter)?.responsibleUser === loggedInUser.id) ||
                // cost center shared users (direct or parent)
                cc.sharedUsers?.includes(loggedInUser.id) ||
                (cc.parentCostCenter && ccIdMap.get(cc.parentCostCenter)?.sharedUsers?.includes(loggedInUser.id)),
        )
    }

    if (searchValue) {
        costCenters = costCenters.filter((costCenter: CostCenter) => {
            const employee = employees.find((e) => e.id === costCenter.responsibleUser)
            const searchables = [
                costCenter.name.toLowerCase(),
                ...(employee ? [employee?.firstName.toLowerCase(), employee?.lastName.toLowerCase()] : []),
                ...(costCenter.id ? [costCenter.id.toLocaleLowerCase()] : []),
            ]
            return searchables.join(" ").includes(searchValue.toLowerCase())
        })

        const searchResultParentCostCenters: Array<any> = findSearchResultParentCostCenters(costCenters).map((costCenterId: string) =>
            allCostCenters.find((costCenter: CostCenter) => costCenter._id === costCenterId),
        )
        costCenters.push(...searchResultParentCostCenters)
    }

    return costCenters.sort((a: CostCenter, b: CostCenter) => a.name.localeCompare(b.name))
}

export const useCostCenterIds = (excludeDeleted: boolean = false, filtered: boolean = false) => useCostCenters(excludeDeleted, filtered).map((cc: CostCenter) => cc._id)

export const useSubCostCenterIds = (excludeDeleted: boolean = false, filtered: boolean = false) => useSubCostCenters(excludeDeleted, filtered).map((cc: CostCenter) => cc._id)

export const useCostCenterById = (id?: string, excludeDeleted: boolean = false) => useCostCenters(excludeDeleted).find((cc: CostCenter) => cc._id === id)

export const useCostCenter = () => useSelector(({ costCenters }: RootState) => costCenters.item)

export const useCostCenterGroups = (excludeDeleted: boolean = false, filtered: boolean = false, searchValue?: string) =>
    useCostCenters(excludeDeleted, filtered, searchValue).filter((costCenter: CostCenter) => costCenter.costCenterType === CostCenterTypeEnum.COST_CENTER_GROUP)

export const useSubCostCenters = (excludeDeleted: boolean = false, filtered: boolean = false, searchValue?: string) =>
    useCostCenters(excludeDeleted, filtered, searchValue).filter((costCenter: CostCenter) => costCenter.costCenterType === CostCenterTypeEnum.SUB_COST_CENTER)
